/**
 * 加载并管理导演项目数据（含分镜、场景、角色）。
 *
 * 提供：
 * - project：完整项目数据（含 shots / scenes / projectCharacters）
 * - loading / error 状态
 * - reload：手动刷新（业务操作后调用）
 *
 * 静默刷新策略（避免后台刷新触发整页 loader 闪烁）：
 * - 仅在「首次加载」或「切换项目」时把 loading 置为 true
 *   （此时把旧 project 清掉，UI 走全屏 loader）
 * - 同一个项目的后续 reload 是静默的：不动 loading、保留旧数据；
 *   失败时也保留旧数据，只在控制台记录，避免把"自动保存后台刷新失败"
 *   误展示为"项目加载失败"
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  directorApi,
  DirectorApiError,
  type DirectorProject,
  type DirectorShotEnriched,
  type DirectorScene,
  type DirectorCharacter,
  type DirectorProp,
} from '../services/directorApi';

export interface DirectorProjectFull extends DirectorProject {
  shots: DirectorShotEnriched[];
  scenes: DirectorScene[];
  projectCharacters: Array<{
    characterId: string;
    sortOrder: number;
    character: DirectorCharacter;
  }>;
  projectProps: Array<{
    propId: string;
    sortOrder: number;
    prop: DirectorProp;
  }>;
}

export interface UseDirectorProjectResult {
  project: DirectorProjectFull | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useDirectorProject(projectId: string | undefined): UseDirectorProjectResult {
  const [project, setProject] = useState<DirectorProjectFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 跟踪当前已成功加载的 projectId；用于区分「首次/切项目加载」与「同项目静默刷新」
  const loadedIdRef = useRef<string | undefined>(undefined);

  const reload = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      loadedIdRef.current = undefined;
      return;
    }

    const isFirstLoad = loadedIdRef.current !== projectId;
    if (isFirstLoad) {
      // 切到不同项目（或第一次进入）：显示全屏 loader、清空旧数据
      setLoading(true);
      setProject(null);
      setError(null);
    }

    try {
      const data = await directorApi.getProject(projectId);
      setProject(data as DirectorProjectFull);
      loadedIdRef.current = projectId;
      if (isFirstLoad) setError(null);
    } catch (err) {
      if (isFirstLoad) {
        setError(err instanceof DirectorApiError ? err.message : '加载失败');
        setProject(null);
      } else {
        // 静默刷新失败：保留现有数据，仅记录
        // eslint-disable-next-line no-console
        console.error('[useDirectorProject] silent reload failed:', err);
      }
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { project, loading, error, reload };
}
